import * as React from 'react';
import { createDefaultApi } from 'src/webportal/utils/createApi';
import { AdminUserSearchResult, DomainWithFilespaces } from 'fs-api-client';
import { Search, SearchType } from './common/Search';
import BackOfficeTopBar from './TopBar';
import { UserSearchResults } from './user/UserSearchResults';
import { FilespaceSearchResults } from './filespace/FilespaceSearchResults';
import { ErrorRenderer } from './common/ErrorRenderer';
import { FETCHING_MESSAGE } from '../utils/constants';
import { renderWaitingForSuccessElement } from '../../webportal/components/common/Icons';
import { injectProfile, ProfileInjectorProperties } from '../../webportal/components/hocs/ProfileInjector';
import { sortFilespaces } from '../utils/utilFunctions';

interface AdminProperties extends ProfileInjectorProperties {
    securityScopes: string [];
}

interface AdminState {
    domains?: DomainWithFilespaces[];
    stats?: any;
    userResults?: AdminUserSearchResult[];
    hasError: boolean;
    searchValue: string;
    errorMessage: string;
    fetching: boolean;
    isSearchInitial: boolean;
    searchType: SearchType;
}

class Dashboard extends React.Component<AdminProperties, AdminState> {
    constructor(props: AdminProperties) {
        super(props);
        this.state = {
            searchValue: '',
            hasError: false,
            errorMessage: '',
            fetching: false,
            isSearchInitial: true,
            searchType: SearchType.Filespace
        };

        this.fetchUsers = this.fetchUsers.bind(this);
        this.fetchFilespaces = this.fetchFilespaces.bind(this);
        this.onUserEmailSearchClick = this.onUserEmailSearchClick.bind(this);
        this.onFilespaceSearchClick = this.onFilespaceSearchClick.bind(this);
        this.onSearchClick = this.onSearchClick.bind(this);
        this.determineFilespaceResultsLength = this.determineFilespaceResultsLength.bind(this);
        this.removeErrorComponent = this.removeErrorComponent.bind(this);
        this.renderHintText = this.renderHintText.bind(this);
        this.setSearchType = this.setSearchType.bind(this);
    }

    componentDidMount() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
                const search = document.getElementById('email-search');
                if (search) {
                    search.focus();
                }
            }
        });
    }

    render() {
        if (this.state.isSearchInitial) {
            return this.renderInitialSearchDashboard();
        }
        return this.renderAdminDashboard();
    }

    private renderAdminDashboard(): JSX.Element {
        return (
            <React.Fragment>
                {this.renderSearchTopBar()}
                <section className="pageBody pageResults">
                    <main className="row">
                        <div className="col-1"/>
                        {this.state.userResults && this.state.searchValue &&
                            <UserSearchResults
                                userSearchResults={this.state.userResults}
                                securityScopes={this.props.securityScopes}
                                fetchUsers={this.fetchUsers}
                            />}
                        {this.state.domains && this.state.searchValue &&
                            <FilespaceSearchResults
                                domains={this.state.domains}
                                securityScopes={this.props.securityScopes}
                            />}
                        {this.state.fetching && renderWaitingForSuccessElement(FETCHING_MESSAGE)}
                        {this.state.hasError && <ErrorRenderer errorMessage={this.state.errorMessage}/>}
                    </main>
                </section>

            </React.Fragment>
        );
    }

    private renderInitialSearchDashboard(): JSX.Element {
        return (
            <React.Fragment>
                {this.renderSearchTopBar()}
                <section className="row pageBody">
                    <main className="row">
                        <div className="col-2">&nbsp;</div>
                        <div className="col-8 row centerAll">
                            <div className="fullWidth">
                                <h1 className="primary">LucidLink Back Office</h1>
                                <h2 className="primary">
                                    Find information about LucidLink accounts and billing plans here.
                                </h2>
                                <Search
                                    onChangeSearchType={this.setSearchType}
                                    onSearchClick={this.onSearchClick}
                                />
                                {this.renderHintText()}
                            </div>
                        </div>
                        <div className="col-2">&nbsp;</div>
                    </main>
                </section>
            </React.Fragment>
        );
    }

    private setSearchType(searchType: SearchType): void {
        this.setState({ searchType });
        return;
    }

    private renderHintText(): JSX.Element {
        if (this.state.searchType === SearchType.Email) {
            return (
                <div className="hint left">
                    A wildcard search that returns all users whose emails contain your search input.
                </div>
            );
        }

        if (this.state.searchType === SearchType.FsUUID) {
            return (
                <div className="hint left">
                    Search by Filespace UUID
                </div>
            );
        }

        return (
            <div className="hint left">
                A wildcard search that returns all Filespaces that meet your search criteria and contain your search
                input.
                <br/>
                <ul className="defaultList">
                    <li>Example: Search by Filespace name
                        - <strong>fsName</strong> or <strong>fsName.domainName</strong></li>
                    <li>Example: Search by domain name - <strong>.domainName</strong> (add a dot in the beginning)</li>
                </ul>
            </div>
        );
    }

    private renderSearchTopBar(): JSX.Element {
        if (this.state.searchValue && this.state.userResults) {
            return (
                <BackOfficeTopBar
                    resultsLength={this.state.userResults.length}
                    searchValue={this.state.searchValue}
                    onSearchClick={this.onSearchClick}
                    isSearchInitial={this.state.isSearchInitial}
                    searchType={this.state.searchType}
                />
            );
        }
        if (this.state.searchValue && this.state.domains) {
            const filespaceResultsLength = this.determineFilespaceResultsLength();
            return (
                <BackOfficeTopBar
                    resultsLength={filespaceResultsLength}
                    searchValue={this.state.searchValue}
                    onSearchClick={this.onSearchClick}
                    isSearchInitial={this.state.isSearchInitial}
                    searchType={this.state.searchType}
                />
            );
        }
        return (
            <BackOfficeTopBar
                isSearchInitial={this.state.isSearchInitial}
                onSearchClick={this.onSearchClick}
                searchValue={this.state.searchValue}
            />
        );
    }

    private determineFilespaceResultsLength(): number {
        let result: number = 0;
        if (this.state.domains) {
            this.state.domains.map((domain: DomainWithFilespaces) => domain.filespaces.map(() => {
                result++;
            }));
        }
        return result;
    }

    private onSearchClick(searchValue: string, searchType: SearchType): void {
        if (!searchValue) {
            return;
        }
        this.setState({ hasError: false });
        if (searchType === SearchType.Filespace) {
            this.onFilespaceSearchClick(searchValue);
        }
        if (searchType === SearchType.Email) {
            this.onUserEmailSearchClick(searchValue);
        }

        if (searchType === SearchType.FsUUID) {
            this.onFSUUIDSearchClick(searchValue);
        }

        this.setState({
                          domains: undefined,
                          stats: undefined,
                          userResults: undefined,
                          hasError: false,
                          errorMessage: '',
                          searchType: searchType,
                          searchValue: searchValue
                      });
    }

    private async onUserEmailSearchClick(searchValue: string): Promise<void> {
        this.setState(
            {
                searchValue: searchValue,
                isSearchInitial: false
            });
        await this.fetchUsers();
    }

    private async onFSUUIDSearchClick(searchValue: string): Promise<void> {
        this.setState(
            {
                searchValue: searchValue,
                isSearchInitial: false
            });
        await this.fetchFilespaces(SearchType.FsUUID);
    }

    private async onFilespaceSearchClick(searchValue: string): Promise<void> {
        this.setState(
            {
                searchValue: searchValue,
                isSearchInitial: false
            }
        );
        await this.fetchFilespaces(SearchType.Filespace);
    }

    private async fetchFilespaces(searchType: SearchType): Promise<void> {
        this.setState({ fetching: true });
        const api = await createDefaultApi();
        try {
            const domains = searchType === SearchType.Filespace ?
                (await api.getFilespacesByFilespaceName(this.state.searchValue)).body :
                (await api.getFilespaceByUUID(this.state.searchValue)).body;
            sortFilespaces(domains);
            if (!domains) {
                this.setState(
                    {
                        hasError: true,
                        errorMessage: `You search result for ${this.state.searchValue} did not yield any results`,
                        fetching: false
                    });
                this.removeErrorComponent();
                return;
            }
            this.setState(
                {
                    domains: domains,
                    isSearchInitial: false,
                    userResults: undefined,
                    fetching: false
                });
        } catch (e) {
            this.setState(
                {
                    hasError: true,
                    errorMessage: `You search result for ${this.state.searchValue} did not yield any results`,
                    fetching: false
                });
            this.removeErrorComponent();
        }
    }

    private async fetchUsers(): Promise<void> {
        this.setState({ fetching: true, userResults: undefined });
        const api = await createDefaultApi();
        try {
            const users = (await api.getUsersDetailsByEmail(this.state.searchValue)).body;
            if (users.length === 0) {
                if (!users) {
                    this.setState({ hasError: true, errorMessage: 'No users were found.' });
                }
                this.removeErrorComponent();
                return;
            }
            this.setState({
                              userResults: users,
                              fetching: false,
                              domains: undefined
                          });
        } catch (e) {
            this.setState(
                {
                    hasError: true,
                    errorMessage: `You search result for ${this.state.searchValue} did not yield any results`,
                    fetching: false
                });
            this.removeErrorComponent();
        }
    }

    private removeErrorComponent(): void {
        setTimeout(
            () => {
                this.setState({ hasError: false, errorMessage: '' });
            },
            5000);
    }

}

const AdminDashboardWithProfile = injectProfile(Dashboard) as any;
export { AdminDashboardWithProfile as AdminDashboard };



// WEBPACK FOOTER //
// ./src/backoffice/components/Dashboard.tsx